import React, { FC, useEffect, useState } from 'react';

import { useStoreon } from 'storeon/react';
import { IEvents, IState } from 'src/store';

import { IProduct } from 'src/api';

import styled, {css} from 'src/theme';
import {Col, Table, Typography} from 'antd';

import { ceilToHundredths, roundToHundredths } from "src/utils/math";
import { usePresenter } from "./usePresenter";

const { Column } = Table;

export const ResultTable: FC = () => {
  const presenter = usePresenter();

  interface ITableData {
    key: number;
    name: string;
    quantity: string | number | null,
    coating?: string,
    ppu: number,
    price: number,
  }

  const initialTableData: ITableData[] = [];
  const [tableData, setTableData] = useState(initialTableData);

  const { fenceStore } = useStoreon<IState, IEvents>('fenceStore');

  useEffect(() => {

    const resultPrices = {
      getPicketPrice: () => {
        const currentPicketProduct = presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Штакетник')
          .filter((product: IProduct) => product.type === fenceStore.picketType)
          .find((product: IProduct) => {
            switch (fenceStore.picketCoating) {
              case 'Полиэстер 0,4 мм':
                return product.thickness === '0,4 Стандарт' && product.coating === 'полиэстер';
              case 'Полиэстер 0,45 мм':
                return product.thickness === '0,45 Стандарт+' && product.coating === 'полиэстер';
              case 'Полиэстер двустор. 0,45 мм':
                return product.thickness === '0,45 Стандарт+' && product.coating === 'полиэстер двустор.';
              case 'Ecosteel 0,5 мм':
                return product.thickness === '0,5' && product.coating === 'Ecosteel';
              case 'Ecosteel двустор. 0,5 мм':
                return product.thickness === '0,5' && product.coating === 'Ecosteel двустор.';
              default:
                return 0;
            }
          });

        return {
          name: 'Штакетник',
          ppu: roundToHundredths(currentPicketProduct?.price ?? 0),
          measure: currentPicketProduct?.unit,
        };
      },

      getPailingPrice: () => {
        const currentPailingProduct = presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Профнастил')
          .filter((product: IProduct) => product.type === fenceStore.pailingType)
          .find((product: IProduct) => {
            switch (fenceStore.pailingCoating) {
              case 'Полиэстер 0,4 мм':
                return product.thickness === '0,4 Стандарт' && product.coating === 'полиэстер';
              case 'Полиэстер 0,45 мм':
                return product.thickness === '0,45 Стандарт+' && product.coating === 'полиэстер';
              case 'Norman MP 0,5 мм':
                return product.thickness === '0,5' && product.coating === 'полиэстер Norman';
              case 'Цинк 0,4 мм':
                return product.thickness === '0,4 Стандарт' && product.coating === 'цинк';
              case 'Цинк 0,45 мм':
                return product.thickness === '0,45 Стандарт+' && product.coating === 'цинк';
              default:
                return 0;
            }
          });

        return {
          name: 'Профнастил',
          ppu: roundToHundredths(currentPailingProduct?.price ?? 0),
          measure: currentPailingProduct?.unit,
        };
      },
      screw: {
        name: 'Саморезы',
        ppu:  roundToHundredths(presenter.priceList.filter((product: IProduct) => product.offer_group === 'Саморезы').find((product: IProduct) => product.type === 'Саморез 4,8х19')?.price || 0),
        measure: presenter.priceList.filter((product: IProduct) => product.offer_group === 'Саморезы').find((product: IProduct) => product.type === 'Саморез 4,8х19')?.unit
      },
      guides: {
        name: 'Направляющие',
        ppu: roundToHundredths(presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Труба профильная')
          .find((product: IProduct) => product.type === fenceStore.pgvValue)?.price ?? 0),
        measure: presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Труба профильная')
          .find((product: IProduct) => product.type === fenceStore.pgvValue)?.unit,
      },
      getPolesPrice: () => {
        const currentPolesProduct = presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Труба профильная')
          .find((product: IProduct) => {
            switch (fenceStore.ssValue) {
              case '50*50*2':
                return product.type === '50*50*2';
              case '60*40*2':
                return product.type === '60*40*2';
              case '60*60*2':
                return product.type === '60*60*2';
              default:
                return 0;
            }
          });

        return {
          name: 'Столбы',
          ppu: roundToHundredths(currentPolesProduct?.price ?? 0),
          measure: currentPolesProduct?.unit,
        };
      },
      plugsOnPoles: {
        name: 'Заглушки на столбы',
        ppu:  roundToHundredths(presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Заглушки на столбы')
          .find((product: IProduct) => {
            switch (fenceStore.ssValue) {
              case '50*50*2':
                return product.type === '50*50';
              case '60*40*2':
                return product.type === '60*40';
              case '60*60*2':
                return product.type === '60*60';
              default:
                return 0;
            }
          })?.price || 0),
        measure: presenter.priceList
          .filter((product: IProduct) => product.offer_group === 'Заглушки на столбы')
          .find((product: IProduct) => {
            switch (fenceStore.ssValue) {
              case '50*50*2':
                return product.type === '50*50';
              case '60*40*2':
                return product.type === '60*40';
              case '60*60*2':
                return product.type === '60*60';
              default:
                return 0;
            }
          })?.unit
      },
      pBar: {
        name: 'Планка П-образная, 2 м',
        ppu: roundToHundredths(presenter.priceList.find((product: IProduct) => product.type === 'Планка П-образная  2 метра')?.price || 0),
        measure: presenter.priceList.find((product: IProduct) => product.type === 'Планка П-образная  2 метра')?.unit
      },
    }

    const tableData: ITableData[] = [];

    if (fenceStore.fenceType === 'pailing') {
      tableData.push(
        {
          key: 1,
          name: `Профнастил ${fenceStore.pailingType} (${fenceStore.pailingCoating})`,
          quantity: `${fenceStore.pCalc} ${resultPrices.getPailingPrice().measure}`,
          coating: fenceStore.pailingCoating,
          ppu: roundToHundredths(resultPrices.getPailingPrice().ppu),
          price: fenceStore.pCalc ? roundToHundredths(fenceStore.pCalc * resultPrices.getPailingPrice().ppu) : 0,
        },
      )
    } else {
      tableData.push(
        {
          key: 1,
          name: `Штакетник ${fenceStore.picketType} (${fenceStore.picketCoating})`,
          quantity: `${fenceStore.pCalc} ${resultPrices.getPicketPrice().measure}`,
          ppu: roundToHundredths(resultPrices.getPicketPrice().ppu),
          price: fenceStore.pCalc ? roundToHundredths(fenceStore.pCalc * resultPrices.getPicketPrice().ppu) : 0,
        },
      )
    }

    if (fenceStore.fenceType === 'pailing' && fenceStore.pailingPBar === 'Нужна') {
      tableData.push(
        {
          key: 2,
          name: 'Планка П-образная, 2 м',
          quantity: `${fenceStore.kppCalc} ${resultPrices.pBar.measure}`,
          ppu: roundToHundredths(resultPrices.pBar.ppu),
          price: fenceStore.kppCalc ? roundToHundredths(fenceStore.kppCalc * resultPrices.pBar.ppu) : 0,
        }
      )
    }

    if (fenceStore.ssValue !== 'Не нужно') {
      tableData.push(
        {
          key: 3,
          name: `Столбы оцинкованные (${fenceStore.ssValue})`,
          quantity: `${fenceStore.dsCalc} ${resultPrices.getPolesPrice().measure}`,
          ppu: roundToHundredths(resultPrices.getPolesPrice().ppu),
          price: fenceStore.dsCalc ? roundToHundredths(fenceStore.dsCalc * resultPrices.getPolesPrice().ppu) : 0,
        }
      );
    }

    if (fenceStore.pgvValue !== 'Не нужно' && fenceStore.pgkValue !== '0') {
      tableData.push(
        {
          key: 4,
          name: `Направляющие (лага оцинкованная) (${fenceStore.pgvValue})`,
          quantity: `${fenceStore.nCalc} ${resultPrices.guides.measure}`,
          ppu: roundToHundredths(resultPrices.guides.ppu),
          price: fenceStore.nCalc ? roundToHundredths(fenceStore.nCalc * resultPrices.guides.ppu) : 0,
        },
      )
    }

    if (fenceStore.ssValue !== 'Не нужно') {
      tableData.push(
        {
          key: 5,
          name: `Заглушки на столбы  (${fenceStore.ssValue})`,
          quantity: `${fenceStore.sCalc} ${resultPrices.plugsOnPoles.measure}`,
          ppu: roundToHundredths(resultPrices.plugsOnPoles.ppu),
          price: fenceStore.sCalc ? roundToHundredths(fenceStore.sCalc * resultPrices.plugsOnPoles.ppu) : 0,
        }
      );
    }

    if (fenceStore.mCalc) {
      tableData.push(
        {
          key: 6,
          name: 'Саморезы (4,8*19)',
          quantity: `${fenceStore.mCalc} ${resultPrices.screw.measure}`,
          ppu: roundToHundredths(resultPrices.screw.ppu),
          price: fenceStore.mCalc ? roundToHundredths(fenceStore.mCalc * resultPrices.screw.ppu) : 0,
        },
      )
    }

    setTableData(tableData);

    }, [
      presenter.priceList,
      fenceStore.fenceType,
      fenceStore.dzValue, // ДЗ - длина забора
      fenceStore.vzValue, // ВЗ - высота забора
      fenceStore.tpValue, // ТП - тип периметра
      fenceStore.rsValue, // РС - расстояние между столбами
      fenceStore.ssValue, // сечение столба
      fenceStore.pCalc, // П - количество листов профнастила
      fenceStore.sCalc, // С - количество столбов
      fenceStore.dsCalc, // ДС - длина столбов
      fenceStore.mCalc, // М - количество саморезов
      fenceStore.nCalc, // Н - длина направляющих
      fenceStore.kvValue, // КВ - количество ворот
      fenceStore.shvValue, // ШВ - ширина ворот
      fenceStore.kkValue, // КК - количество калиток
      fenceStore.shkValue, // ШК - ширина калитки
      fenceStore.kpCalc, // КП - количество пролетов
      fenceStore.kppCalc, // КПП - количество П-планок
      fenceStore.pgvValue, // ПГВ - параметр горизонталей вид
      fenceStore.pgkValue, // ПГК - параметр горизонталей количество
      fenceStore.kValue, // крепления
      fenceStore.pailingCoating, // Параметры профнастила - Тип покрытия
      fenceStore.pailingType, // Параметры профнастила - Вид профнастила
      fenceStore.pailingPBar, // Параметры профнастила - П-образная планка
      fenceStore.picketCoating, // Параметры штакетника - Тип покрытия
      fenceStore.picketType, // Параметры штакетника - Вид штакетника
    ]);

  useEffect(() => {
    const totalPrice = ceilToHundredths(tableData.reduce((acc, cur) => acc + cur.price, 0));
    presenter.setTotalPrice(totalPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  return (
    <div>
      <PDFHiddenStyled id={"pdf_hidden"}>
        <PDFLayoutStyled id={"pdf_for_save"}>
          <PDFHeaderStyled>Предварительный расчет проекта</PDFHeaderStyled>
          <PDFTableStyled is_mobile={0}>

            <Table
              dataSource={tableData}
              pagination={false}
            >
              <Column title="Наименование" dataIndex="name" key="name" />
              <Column title="Кол-во" dataIndex="quantity" key="quantity" />
              <Column title="Цена за ед., р." dataIndex="ppu" key="ppu" />
              <Column title="Стоимость, р." dataIndex="price" key="price" />
            </Table>

            <PDFTotalStyled>
              Итого: {presenter.totalPrice} руб
            </PDFTotalStyled>
          </PDFTableStyled>

          <PDFRemarkStyled>
            <Typography.Paragraph>
              Вся приведенная информация,
              включая информацию о ценах, носит исключительно рекламно-информационный характер
              и не является публичной офертой. Точную информацию о ценах и ассортименте уточняйте
              в офисах продаж и по телефонам.
            </Typography.Paragraph>
          </PDFRemarkStyled>
        </PDFLayoutStyled>
      </PDFHiddenStyled>

      <Table
        dataSource={tableData}
        pagination={false}
      >
        <Column title="Наименование" dataIndex="name" key="name" />
        <Column title="Кол-во" dataIndex="quantity" key="quantity" />
        <Column title="Цена за ед., р." dataIndex="ppu" key="ppu" />
        <Column title="Стоимость, р." dataIndex="price" key="price" />
      </Table>

      <DivStyled>
        Итого: {presenter.totalPrice} руб
      </DivStyled>
    </div>
  );
};

const PDFTableStyled = styled(Col)<{ is_mobile: number }>`
  ${(props) => css`
    & .ant-table-wrapper table {
      font-size: ${props.is_mobile ? '10px' : '12px'};
    }
    & .ant-table-wrapper td {
      padding: ${props.is_mobile ? '2px' : '5px'};
    }
  `}
`;

const PDFHiddenStyled = styled('div')`
    position: relative;
    top: 0;
    left: 0;
    overflow: hidden;
`;
const PDFLayoutStyled = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1000;
    padding: 10px;
    width: 500px;
    height: 500px;
    background-color: white;
    font-size: 10px;
`;

const PDFRemarkStyled = styled(Col)`
  margin-top: 20px;
  font-size: 9px;
  color:  ${props => props.theme.colors.smoke};
`;

const PDFHeaderStyled = styled.h4`
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  margin-bottom: 15px;
  color: ${props => props.theme.colors.orange};
`;

const PDFTotalStyled = styled('div')`
  font-size: 16px;
  text-align: right;
  padding: 10px;
`;



const DivStyled = styled('div')`
  font-size: 16px;
  text-align: right;
  padding: 10px;
`;
